import { UserAgentApplication } from 'msal';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useToasts } from 'react-toast-notifications';

import { frontoffice, parseNavigatorLanguage, successResponse } from 'core';
import { useRouter } from 'core/hooks/useRouter';
import { loginNoPublisher, loginSSO, signupUser, validateLicense } from 'core/crud/auth.crud';
import { getSignUpLicenseErrorMsg, SIGN_UP_LICENSE_ERRORS } from 'core/modules/components/forms/AddLicenseForm/signUpLicenseErrors';
import * as coreEntities from 'core/store/index';
import { parseProgramsSignup } from 'core/utils/parses';
import type { CrudResponse } from 'core/utils/response';
import { licenseType, role } from 'core/variables/constant';

import type {
  Authorization,
  Classroom,
  Identity,
  IdentityProvider,
  LicenseData,
  LicenseItem,
  LicenseResponse,
  LoginResponse,
  SignUpUser,
  SignUpViewProps,
  UseSignUp,
  UseSignUpParams,
} from './types';

type SignUpResponse = CrudResponse & { error?: { data?: { status?: string; error?: { error?: string } } } };

const MICROSOFT_REQUEST = { scopes: ['user.read'], prompt: 'select_account' };
const LOGIN_DELAY_MS = 1000;

function splitFullName(fullName?: string): Pick<Identity, 'name' | 'surname'> {
  const name = (fullName ?? '').trim();
  const [first, second] = name.split(/\s+/);
  return second ? { name: first, surname: second } : { name, surname: name };
}

async function acquireMicrosoftIdentity(app: UserAgentApplication): Promise<Identity> {
  try {
    const { account, accessToken } = await app.acquireTokenSilent(MICROSOFT_REQUEST);
    return { email: account.userName, ...splitFullName(account.name), token: accessToken };
  } catch {
    const { account, accessToken } = await app.acquireTokenPopup(MICROSOFT_REQUEST);
    // El nombre completo se guarda tal cual también como apellido: así llegaba ya a la cuenta.
    return { email: account.userName, name: account.name, surname: account.name, token: accessToken };
  }
}

function onCreateClassroom(): void {
  // SignUpView la llama sin comprobar; las dos páginas ya la tenían vacía antes de unificarlas.
}

/** Estado y acciones del alta, comunes a la página de app y a la de Edelvives. */
function useSignUp({ addLicenseWithoutContent = false, getMicrosoftClientId }: UseSignUpParams): UseSignUp {
  const router = useRouter();
  const dispatch = useDispatch();
  const { t } = useTranslation(['signup', 'common']);
  const { addToast } = useToasts();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(0);
  const [licenseCodes, setLicenseCodes] = useState<string[]>([]);
  const [licenses, setLicenses] = useState<LicenseItem[]>([]);
  const [classrooms, setClassrooms] = useState<Classroom[]>([]);
  const [user, setUser] = useState<SignUpUser>({
    name: null,
    surname: null,
    password: null,
    email: null,
    terms: false,
    role: null,
    birthdate: null,
    identity_provider: null,
    token: null,
    lang_id: parseNavigatorLanguage(navigator.language),
  });
  const [authorization, setAuthorization] = useState<Authorization>(null);
  const [errorForm, setErrorForm] = useState<Record<string, string>>({});
  const [isSkippable, setIsSkippable] = useState(false);
  const [licenseErrorMsg, setLicenseErrorMsg] = useState<string | null>(null);
  const educationLevels = useSelector((state) => frontoffice.educationLevels.selectors.getEducationLevels(state));
  const disciplines = useSelector((state) => frontoffice.educationLevels.selectors.getDisciplines(state));

  useEffect(() => {
    setIsSkippable(step === 4 && classrooms.length === 0);
  }, [classrooms]);

  useEffect(() => {
    if (licenses.length === 0) setUser((current) => ({ ...current, role: null }));
  }, [licenses]);

  function onError(text: string): void {
    addToast(text, { appearance: 'error', autoDismiss: true });
    setLoading(false);
  }

  // Sin campo al que asociarlo: el aviso se queda hasta que se cierra y lo anuncia la región de los toasts.
  function onSignUpError(): void {
    setLoading(false);
    addToast(t('common:An error has occurred'), { appearance: 'error' });
  }

  function addPersonalLicense(license: LicenseData): void {
    try {
      if (user.role && user.role !== license.role_guid) {
        setLicenseErrorMsg(SIGN_UP_LICENSE_ERRORS.otherProfile);
        return;
      }
      const withoutContent = license.programs.length === 0;
      if (withoutContent) setLicenseErrorMsg(SIGN_UP_LICENSE_ERRORS.withoutContent);
      if (withoutContent && !addLicenseWithoutContent) return;
      setLicenses(parseProgramsSignup([...licenses, ...license.programs]));
      setLicenseCodes([...licenseCodes, license.code]);
      setUser({ ...user, role: license.role_guid });
    } catch {
      onError(t('common:An error has occurred'));
    }
  }

  function addClassroomLicense(license: LicenseData): void {
    if (step === 0) {
      setLicenseErrorMsg(SIGN_UP_LICENSE_ERRORS.codeNotFound);
      return;
    }
    const level = `${license.education_year_name} ${license.education_level_name}`;
    setClassrooms([...classrooms, { name: license.name, code: license.code, level }]);
    setLicenseCodes([...licenseCodes, license.code]);
  }

  async function onCheckLicense({ code }: { code: string }): Promise<void> {
    setLicenseErrorMsg(null);
    if (licenseCodes.includes(code)) {
      setLicenseErrorMsg(SIGN_UP_LICENSE_ERRORS.alreadyAdded);
      return;
    }
    setLoading(true);
    const response = (await validateLicense(code)) as LicenseResponse;
    setLoading(false);
    if (response?.data?.status !== 'success') {
      setLicenseErrorMsg(getSignUpLicenseErrorMsg(response));
    } else if (response.data.data.type === licenseType.personal) {
      addPersonalLicense(response.data.data);
    } else if (response.data.data.type === licenseType.school) {
      addClassroomLicense(response.data.data);
    }
  }

  function onChangeLicenseValue(): void {
    if (licenseErrorMsg) setLicenseErrorMsg(null);
  }

  function onDeleteLicense(item: LicenseItem): void {
    setLicenses(licenses.filter((license) => license.code !== item.code));
    setLicenseCodes(licenseCodes.filter((license) => license !== item.code));
  }

  function onCleanLicenses(): void {
    setLicenses([]);
    setLicenseCodes([]);
  }

  function onDeleteClass(code: string): void {
    setClassrooms(classrooms.filter((classroom) => classroom.code !== code));
    setLicenseCodes(licenseCodes.filter((license) => license !== code));
  }

  function doLogin(data: LoginResponse): void {
    if (data?.data?.data?.token) {
      setLoading(true);
      dispatch(coreEntities.auth.actions.login(data.data.data));
    } else {
      onSignUpError();
    }
  }

  async function signUp(): Promise<void> {
    setLoading(true);
    const payload: Record<string, unknown> = {
      role: user.role === role.student ? 'student' : 'teacher',
      name: user.name.trim(),
      lastname: user.surname.trim(),
      email: user.email.trim(),
      username: user.email.trim(),
      licenses: licenseCodes,
      lang_id: user.lang_id,
    };

    if (user.identity_provider) {
      payload.sso_token = user.token;
      payload.identity_provider = user.identity_provider;
    } else {
      payload.password = user.password.trim();
    }

    if (authorization?.need) {
      payload.parent_consent = 1;
      payload.parent_email = authorization.parentEmail;
    }

    const response = (await signupUser(payload)) as SignUpResponse;
    if (successResponse(response)) {
      setTimeout(() => {
        const login = user.identity_provider ? loginSSO(user.token, user.identity_provider) : loginNoPublisher(user.email, user.password.trim());
        login.then(doLogin).catch(onSignUpError);
      }, LOGIN_DELAY_MS);
    } else if (response?.error?.data?.status === 'fail' && response.error.data.error?.error === 'USERNAME_EXIST') {
      setErrorForm({ email: t('signup:User already exists') });
      setLoading(false);
      setStep(2);
    } else {
      onSignUpError();
    }
  }

  function identify(provider: IdentityProvider, identity: Identity): void {
    setUser((current) => ({ ...current, ...identity, identity_provider: provider }));
  }

  async function onLoginMicrosoft(_onSubmitSSO: unknown, onErrorSSO?: (error: unknown) => void): Promise<void> {
    const clientId = getMicrosoftClientId(globalThis.location.origin);
    const app = new UserAgentApplication({ auth: { clientId, redirectUri: globalThis.location.origin } });
    try {
      await app.loginPopup(MICROSOFT_REQUEST);
    } catch (error) {
      onErrorSSO?.(error);
      return;
    }
    try {
      identify('microsoft', await acquireMicrosoftIdentity(app));
    } catch (error) {
      onErrorSSO?.(error);
    }
  }

  function returnInTheFirstStep(): void {
    router.history.push('/auth/login');
  }

  const viewProps: SignUpViewProps = {
    changeStep: setStep,
    changeUser: setUser,
    changeAuthorization: setAuthorization,
    onEnd: signUp,
    changeErrorForm: setErrorForm,
    cleanLicenses: onCleanLicenses,
    changeIsSkippable: setIsSkippable,
    onCreateTeacher: signUp,
    step,
    onCheckLicense,
    licenseErrorMsg,
    onChangeLicenseValue,
    licenses,
    licenseCodes,
    onDeleteLicense,
    user,
    authorization,
    classrooms,
    onDeleteClass,
    errorForm,
    onLoginMicrosoft,
    isSkippable,
    onCreateClassroom,
    educationLevels,
    disciplines,
    returnInTheFirstStep,
  };

  return { loading, viewProps, identify };
}

export default useSignUp;
